import React from 'react';
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";

const ChartProgress = withStyles({
  colorPrimary: { backgroundColor: '#d6e4f0' },
  barColorPrimary: { backgroundColor: '#337ab7' },
})(LinearProgress);

const ChartLoading = () => (
  <Box py={2} px={2} style={{ color: '#666', minWidth: 240 }}>
    <Typography variant="body2" style={{ marginBottom: 6, fontSize: 16 }}>
      Loading chart data......
    </Typography>
    <ChartProgress />
  </Box>
);

const DEFAULT_WIDTH = 600;
const DEFAULT_BAR_HEIGHT = 15;
const DEFAULT_SPACER = 8;

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizeGraphic = (graphic) => {
  if (!graphic) return null;
  const d = {
    QUERY_L: toNumber(graphic.QUERY_L),
    RESULT_OOBQ: toNumber(graphic.RESULT_OOBQ),
    RESULT_OOEQ: toNumber(graphic.RESULT_OOEQ),
    SUBJECT_L: toNumber(graphic.SUBJECT_L),
    RESULT_OOBD: toNumber(graphic.RESULT_OOBD),
    RESULT_OOED: toNumber(graphic.RESULT_OOED)
  };
  if (Object.values(d).some(v => v === null)) return null;
  if (d.RESULT_OOBQ > d.RESULT_OOEQ) {
    const t = d.RESULT_OOBQ;
    d.RESULT_OOBQ = d.RESULT_OOEQ;
    d.RESULT_OOEQ = t;
  }
  if (d.RESULT_OOBD > d.RESULT_OOED) {
    const t = d.RESULT_OOBD;
    d.RESULT_OOBD = d.RESULT_OOED;
    d.RESULT_OOED = t;
  }
  return d;
};

const buildLayoutFromGraphic = (graphic, width) => {
  const d = normalizeGraphic(graphic);
  if (!d) return null;

  const w = toNumber(width) || DEFAULT_WIDTH;
  const left_qry = d.RESULT_OOBQ - 1;
  const left_sbj = d.RESULT_OOBD - 1;
  const over_qry = d.RESULT_OOEQ - d.RESULT_OOBQ + 1;
  const over_sbj = d.RESULT_OOED - d.RESULT_OOBD + 1;
  const right_qry = d.QUERY_L - d.RESULT_OOEQ;
  const right_sbj = d.SUBJECT_L - d.RESULT_OOED;

  const left_max = Math.max(left_qry, left_sbj);
  const over_max = Math.max(over_qry, over_sbj);
  const right_max = Math.max(right_qry, right_sbj);
  const total = left_max + over_max + right_max;
  const scale = total > 0 ? (w / total) : 0;

  return {
    width: w,
    barHeight: DEFAULT_BAR_HEIGHT,
    spacer: DEFAULT_SPACER,
    subject: {
      left: { x: scale * (left_max - left_sbj), width: scale * left_sbj },
      overlap: { x: scale * left_max, width: scale * over_max },
      right: { x: scale * (left_max + over_max), width: scale * right_sbj }
    },
    query: {
      left: { x: scale * (left_max - left_qry), width: scale * left_qry },
      overlap: { x: scale * left_max, width: scale * over_max },
      right: { x: scale * (left_max + over_max), width: scale * right_qry }
    }
  };
};

// Single-hit view matching the legacy alignmentChart canvas directive:
// one query bar (light blue, dark blue overlap) above one subject bar
// (grey, dark grey overlap) showing where the query hits the subject.
export function ClassicAlignmentChart({ item, width }) {
  const layout = item?.RESULT_GRAPHIC && buildLayoutFromGraphic(item.RESULT_GRAPHIC, width);
  if (!layout) return <ChartLoading />;

  const { width: w, barHeight, spacer, subject, query } = layout;

  return (
    <svg width={w} height={barHeight * 2 + spacer}>
      <g>
        <rect x={query.left.x} width={query.left.width} height={barHeight} fill="#66ADEA" />
        <rect x={query.overlap.x} width={query.overlap.width} height={barHeight} fill="#337ab7" />
        <rect x={query.right.x} width={query.right.width} height={barHeight} fill="#66ADEA" />
      </g>
      <g transform={`translate(0, ${barHeight + spacer})`}>
        <rect x={subject.left.x} width={subject.left.width} height={barHeight} fill="#CCCCCC" />
        <rect x={subject.overlap.x} width={subject.overlap.width} height={barHeight} fill="#999999" />
        <rect x={subject.right.x} width={subject.right.width} height={barHeight} fill="#CCCCCC" />
      </g>
    </svg>
  );
}

function AlignmentChart({ data, selectedQueryId, highlightedQueryId, onRectHighlight, queryColorMap, colorMode }) {
  //console.log('Received data for AlignmentChart:', data);
  if (!Array.isArray(data) || !data.length) {
    return <ChartLoading />;
  }

  const normalized = data
    .map((item) => {
      const layout = item?.chart_layout || buildLayoutFromGraphic(item?.RESULT_GRAPHIC, item?.chartWidth || DEFAULT_WIDTH);
      if (!layout || !layout.query || !layout.subject || !layout.query.overlap) {
        return null;
      }
      return {
        id: item?.id || item?.query_id || Math.random().toString(36), q_id: item?.query_id,
        layout
      };
    })
    .filter(Boolean);

  if (!normalized.length) {
    return <ChartLoading />;
  }

  const { width, barHeight, spacer } = normalized[0].layout;
  const subject = normalized[0].layout.subject;

  const queries = normalized.map(item => {
    const overlap = item.layout.query.overlap;
    return {
      id: item.id,
      x: overlap.x,
      width: overlap.width,
      q_id: item.q_id
    };
  });


  queries.sort((a, b) => a.x - b.x);

  const isSelected = (q) => q.q_id === selectedQueryId || q.id === selectedQueryId;

  const getBarColor = (q) => {
    if (colorMode) {
      const mapped = queryColorMap && queryColorMap[q.q_id?.toUpperCase()];
      return mapped || '#999999';
    }
    const selected = isSelected(q);
    const highlighted = !selected && (q.q_id === highlightedQueryId || q.id === highlightedQueryId);
    if (selected) return '#337ab7';
    if (highlighted) return '#ff8c00';
    return '#999999';
  };

  // Lane packing (no overlaps or same position per row)
  const lanes = [];

  queries.forEach(q => {
    let placed = false;

    for (let lane of lanes) {
      const last = lane[lane.length - 1];
      const lastEnd = last.x + last.width;
      //console.log(`Trying to place query ${q.q_id} in lane with last query ${last.q_id}: lastEnd=${lastEnd}, q.x=${q.x}`);

      // Place in this lane only if q.x > lastEnd (no overlap, not same position)
      if (q.x > lastEnd) {
        lane.push(q);
        placed = true;
        break;
      }
    }

    if (!placed) {
      lanes.push([q]);
    }
  });

  const rows = [];

  // Subject row
  rows.push({
    type: 'subject',
    y: 0
  });

  // Query lanes
  const LANE_VERTICAL_SPACING = barHeight + spacer; 
  let currentY = barHeight + spacer;

  lanes.forEach((lane, idx) => {
    rows.push({
      type: 'lane',
      lane,
      y: currentY,
      key: `lane-${idx}`
    });

    currentY += LANE_VERTICAL_SPACING;
  });

  const svgHeight = currentY + barHeight + spacer;

 
  return (
    <div>
      <svg width={width} height={svgHeight}>

        {rows.map((row, i) => {
          if (row.type === 'subject') {
            return (
              <g key="subject" transform={`translate(0, ${barHeight + spacer})`}>
                <rect
                  x={subject.left.x}
                  width={subject.left.width}
                  height={barHeight}
                  fill="#CCCCCC"
                />
                <rect
                  x={subject.overlap.x}
                  width={subject.overlap.width}
                  height={barHeight}
                  fill="#CCCCCC"
                />
                <rect
                  x={subject.right.x}
                  width={subject.right.width}
                  height={barHeight}
                  fill="#CCCCCC"
                />
              </g>
            );
          }

          if (row.type === 'lane') {
            return (
              <g key={row.key} transform={`translate(0, ${row.y + barHeight + spacer})`}>
                {row.lane.map(q => {
                  return (
                    <Tooltip key={q.id} title={`Query ID: ${q.q_id}`} placement="top">
                      <rect
                        key={q.id}
                        x={q.x}
                        width={q.width}
                        height={barHeight}
                        fill={getBarColor(q)}
                        style={{ cursor: isSelected(q) ? 'default' : 'pointer' }}
                        onClick={() => { if (isSelected(q)) onRectHighlight && onRectHighlight(null); else onRectHighlight && onRectHighlight(q.q_id || q.id); }}
                      />
                    </Tooltip>
                  );
                })}
              </g>
            );
          }

          return null;
        })}
      </svg>
    </div>
  );
}

export default AlignmentChart;